import * as Phaser from "phaser"
import type { EnemyData, GameSprite } from "../../types"
import {
  type EnemyState,
  type EnemyStateContext,
  StatefulEnemyRuntime,
} from "../enemy-system"

type SynapseNodeFsmState = "orbiting" | "dead"

export class SynapseNodeEnemy extends StatefulEnemyRuntime<SynapseNodeFsmState> {
  private orbitAngle = 0
  private orbitRadius = 300 // Further increased for hitability and visibility
  private orbitSpeed = 0.001 // Slower, more readable orbit
  private parentSprite: GameSprite | null = null

  constructor(sprite: GameSprite, enemyData: EnemyData) {
    const states: EnemyState<SynapseNodeFsmState>[] = [
      {
        name: "orbiting",
        behaviorState: "hovering",
        canFire: true,
        enter: (ctx) => {
          this.orbitAngle = Math.random() * Math.PI * 2
          this.findParent(ctx)
        },
        update: (ctx) => {
          if (!this.parentSprite || !this.parentSprite.active) {
            this.findParent(ctx)
          }

          if (!this.parentSprite || !this.parentSprite.active) {
            // Only die if parent is truly gone after some time
            if (ctx.stateTime > 2000) {
              ctx.enemyData.hp = 0
              return "dead"
            }
            return null
          }

          this.orbitAngle += this.orbitSpeed * ctx.delta
          const targetX =
            this.parentSprite.x + Math.cos(this.orbitAngle) * this.orbitRadius
          const targetY =
            this.parentSprite.y + Math.sin(this.orbitAngle) * this.orbitRadius

          // Lerp toward orbit position
          ctx.sprite.x += (targetX - ctx.sprite.x) * 0.05
          ctx.sprite.y += (targetY - ctx.sprite.y) * 0.05

          ctx.sprite.setAngle(Phaser.Math.RadToDeg(this.orbitAngle) + 90)
          ctx.sprite.setAlpha(1)

          // Visual connection beam
          this.drawConnectionBeam(ctx)

          return null
        },
      },
      {
        name: "dead",
        behaviorState: "dying",
        enter: (ctx) => {
          ctx.sprite.destroy()
        },
        update: () => null,
      },
    ]

    super(sprite, enemyData, "orbiting", states)
  }

  private findParent(ctx: EnemyStateContext<SynapseNodeFsmState>): void {
    if (ctx.enemyData.parentId) {
      const children = ctx.scene.children.list
      for (const child of children) {
        const s = child as GameSprite
        if (s.active && s.getData("id") === ctx.enemyData.parentId) {
          this.parentSprite = s
          break
        }
      }
    }
  }

  private drawConnectionBeam(
    ctx: EnemyStateContext<SynapseNodeFsmState>,
  ): void {
    if (!this.parentSprite) return

    // Emit particles along the line from node to brain
    const count = 3
    for (let i = 0; i < count; i++) {
      const t = (ctx.time * 0.005 + i / count) % 1
      const px = Phaser.Math.Linear(ctx.sprite.x, this.parentSprite.x, t)
      const py = Phaser.Math.Linear(ctx.sprite.y, this.parentSprite.y, t)

      ctx.vfx.emitImpactBurst(px, py, 0x06b6d4, 0.4)
    }

    // Random energy sparks
    if (Math.random() < 0.1) {
      ctx.vfx.emitImpactBurst(
        ctx.sprite.x + (Math.random() - 0.5) * 40,
        ctx.sprite.y + (Math.random() - 0.5) * 40,
        0xffffff,
        0.3,
      )
    }
  }
}
